import { Collapse, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { UseTransientScenariosReturn } from "../hooks/useTransientScenarios";
import type { DayNightFlushingConfig, ScenarioDefinitionFormState } from "../model/transientScenario";

import { ConfigurationSwitchRow } from "./ConfigurationSwitchRow";
import { ScenarioDayNightFlushingSettings } from "./ScenarioDayNightFlushingSettings";
import { ScenarioDropdownColumn } from "./ScenarioDropdownColumn";
import { ScenarioSwitchColumn } from "./ScenarioSwitchColumn";

const EMPTY_FLUSHING: DayNightFlushingConfig = {
  bypass: false,
  enable_day_flushing: false,
  enable_night_flushing: false,
  flush_temp_lower_day_limit: 0,
  flush_temp_lower_night_limit: 0,
  flush_temp_upper_day_limit: 0,
  flush_temp_upper_night_limit: 0,
  flushing_day_hours: [],
  flushing_night_hours: [],
  flushing_t_exhaust_day_threshold: 0,
  flushing_t_exhaust_night_threshold: 0,
};

type ScenarioDefinitionFieldsProps = {
  scenario: ScenarioDefinitionFormState;
  scenariosState: UseTransientScenariosReturn;
};

export const ScenarioDefinitionFields = ({ scenario, scenariosState }: ScenarioDefinitionFieldsProps) => {
  const { t } = useTranslation();
  const { updateScenario } = scenariosState;
  const [isFlushingEnabled, setIsFlushingEnabled] = useState(scenario.dayNightFlushing != null);
  const [flushingDraft, setFlushingDraft] = useState(scenario.dayNightFlushing ?? EMPTY_FLUSHING);

  useEffect(() => {
    if (scenario.dayNightFlushing) setFlushingDraft(scenario.dayNightFlushing);
  }, [scenario.dayNightFlushing]);

  const onDayNightFlushingChange = <K extends keyof DayNightFlushingConfig>(
    key: K,
    value: DayNightFlushingConfig[K]
  ) => {
    const dayNightFlushing = { ...flushingDraft, [key]: value };
    setFlushingDraft(dayNightFlushing);
    if (isFlushingEnabled) updateScenario(scenario.id, { dayNightFlushing });
  };

  return (
    <Stack className="w-full flex-col gap-8" direction="column">
      <div className="grid w-full grid-cols-2 gap-10">
        <ScenarioDropdownColumn scenario={scenario} scenariosState={scenariosState} />
        <ScenarioSwitchColumn scenario={scenario} scenariosState={scenariosState} />
      </div>
      <Stack className="w-full flex-col gap-4" direction="column">
        <ConfigurationSwitchRow
          checked={isFlushingEnabled}
          label={t("Execution.transient.scenario.dayNightFlushing.enable")}
          onChange={(enabled) => {
            setIsFlushingEnabled(enabled);
            updateScenario(scenario.id, {
              dayNightFlushing: enabled ? flushingDraft : null,
            });
          }}
        />
        <Collapse in={isFlushingEnabled} timeout={300}>
          <ScenarioDayNightFlushingSettings
            dayNightFlushing={flushingDraft}
            onDayNightFlushingChange={onDayNightFlushingChange}
          />
        </Collapse>
      </Stack>
    </Stack>
  );
};
